import React, { useState, useMemo, useEffect } from 'react';
import { getCategoryColor, getMarkerColor, setCategoryColor, getHiddenGroups, setHiddenGroups as setGlobalHiddenGroups } from '../utils/markerUtils';
import './FilterPanel.css';

function FilterPanel({ markers, sheetConfig, onFilterChange, onColorChange, onGroupVisibilityChange }) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [hiddenGroups, setHiddenGroups] = useState(() => {
    // Initialize from saved state if available
    return getHiddenGroups();
  });
  const [initialized, setInitialized] = useState(false);
  const [editingColorGroup, setEditingColorGroup] = useState(null);
  const [tempColor, setTempColor] = useState(null);
  const [colorRefreshKey, setColorRefreshKey] = useState(0);

  // Sync hidden groups from global state and reset initialized when markers change
  useEffect(() => {
    const restoredHiddenGroups = getHiddenGroups();
    setHiddenGroups(restoredHiddenGroups);
    setInitialized(false);
  }, [markers]);

  // Initialize with filtered markers on mount, respecting saved hidden groups
  useEffect(() => {
    if (!initialized && markers.length > 0) {
      // If there are hidden groups, apply the filter
      if (hiddenGroups.size > 0) {
        const styleField = sheetConfig?.styleColumn?.toLowerCase();
        const visibleMarkers = markers.filter(m => {
          if (!styleField) {
            const color = getMarkerColor(m.color);
            return !hiddenGroups.has(color);
          }
          const markerField = Object.keys(m).find(key => key.toLowerCase() === styleField);
          const value = markerField ? String(m[markerField] || 'None') : 'None';
          return !hiddenGroups.has(value);
        });
        onFilterChange(visibleMarkers);
      } else {
        // No hidden groups, show all markers
        onFilterChange(markers);
      }
      setInitialized(true);
    }
  }, [initialized, markers, onFilterChange, hiddenGroups, sheetConfig]);

  // Group markers by their style column value
  const colorGroups = useMemo(() => {
    if (!sheetConfig?.styleColumn) {
      // No style column configured, group by color as fallback
      const groups = {};
      markers.forEach(marker => {
        const color = getMarkerColor(marker.color);
        if (!groups[color]) {
          groups[color] = {
            color,
            markers: [],
            label: color === '#4285F4' ? 'Default' : color.toUpperCase(),
            groupKey: color
          };
        }
        groups[color].markers.push(marker);
      });
      return Object.values(groups).sort((a, b) => a.label.localeCompare(b.label));
    }

    // Group by the style column's field value
    const styleField = sheetConfig.styleColumn.toLowerCase();
    const groups = {};

    markers.forEach(marker => {
      // Find the style field value (case-insensitive)
      const markerField = Object.keys(marker).find(key => key.toLowerCase() === styleField);
      const value = markerField ? String(marker[markerField] || 'None') : 'None';

      // Compute color the same way as in Map/DeckGLMap
      const color = getCategoryColor(value);

      if (!groups[value]) {
        groups[value] = {
          color,
          markers: [],
          label: value,
          groupKey: value
        };
      }

      groups[value].markers.push(marker);
    });

    // Sort alphabetically by label
    return Object.values(groups).sort((a, b) => a.label.localeCompare(b.label));
  }, [markers, sheetConfig, colorRefreshKey]);

  // Re-apply filter when groups change (e.g., after marker updates)
  useEffect(() => {
    if (!initialized) return;

    // Clean up hiddenGroups - remove any groups that no longer exist
    const currentGroupKeys = new Set(colorGroups.map(g => g.groupKey));
    const newHiddenGroups = new Set(
      Array.from(hiddenGroups).filter(key => currentGroupKeys.has(key))
    );

    // Only update hiddenGroups state if something changed
    if (newHiddenGroups.size !== hiddenGroups.size) {
      setHiddenGroups(newHiddenGroups);
    }

    // Re-apply the current filter with updated groups
    // If all groups are hidden (hiddenGroups size equals total groups), show nothing
    if (newHiddenGroups.size === colorGroups.length && colorGroups.length > 0) {
      onFilterChange([]);
      return;
    }

    const styleField = sheetConfig?.styleColumn?.toLowerCase();
    const visibleMarkers = markers.filter(m => {
      if (!styleField) {
        const color = getMarkerColor(m.color);
        return !newHiddenGroups.has(color);
      }
      const markerField = Object.keys(m).find(key => key.toLowerCase() === styleField);
      const value = markerField ? String(m[markerField] || 'None') : 'None';
      return !newHiddenGroups.has(value);
    });
    onFilterChange(visibleMarkers);
  }, [colorGroups]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleToggleGroup = (groupKey) => {
    const newHiddenGroups = new Set(hiddenGroups);

    if (newHiddenGroups.has(groupKey)) {
      newHiddenGroups.delete(groupKey);
    } else {
      newHiddenGroups.add(groupKey);
    }

    setHiddenGroups(newHiddenGroups);
    // Update global state for persistence
    setGlobalHiddenGroups(newHiddenGroups);

    // If all groups are hidden, show nothing
    if (newHiddenGroups.size === colorGroups.length && colorGroups.length > 0) {
      onFilterChange([]);
      // Notify parent to save changes
      if (onGroupVisibilityChange) {
        onGroupVisibilityChange();
      }
      return;
    }

    // Notify parent of filtered markers
    const styleField = sheetConfig?.styleColumn?.toLowerCase();
    const visibleMarkers = markers.filter(m => {
      if (!styleField) {
        // Fallback to color-based filtering
        const color = getMarkerColor(m.color);
        return !newHiddenGroups.has(color);
      }
      // Filter by style field value
      const markerField = Object.keys(m).find(key => key.toLowerCase() === styleField);
      const value = markerField ? String(m[markerField] || 'None') : 'None';
      return !newHiddenGroups.has(value);
    });
    onFilterChange(visibleMarkers);

    // Notify parent to save changes
    if (onGroupVisibilityChange) {
      onGroupVisibilityChange();
    }
  };

  const handleToggleAll = (show) => {
    if (show) {
      const emptySet = new Set();
      setHiddenGroups(emptySet);
      setGlobalHiddenGroups(emptySet);
      onFilterChange(markers);
    } else {
      const allGroupKeys = new Set(colorGroups.map(g => g.groupKey));
      setHiddenGroups(allGroupKeys);
      setGlobalHiddenGroups(allGroupKeys);
      onFilterChange([]);
    }

    // Notify parent to save changes
    if (onGroupVisibilityChange) {
      onGroupVisibilityChange();
    }
  };

  const handleColorInputChange = (newColor, event) => {
    event.stopPropagation(); // Prevent toggle when changing color
    setTempColor(newColor);
  };

  const handleSetColor = (groupKey, event) => {
    event.stopPropagation(); // Prevent toggle when clicking button

    if (tempColor) {
      // Update the color assignment
      setCategoryColor(groupKey, tempColor);

      // Force re-render by updating refresh key
      setColorRefreshKey(prev => prev + 1);

      // Notify parent component to trigger map re-render
      if (onColorChange) {
        onColorChange();
      }
    }

    // Close the color picker
    setEditingColorGroup(null);
    setTempColor(null);
  };

  const handleColorClick = (groupKey, currentColor, event) => {
    event.stopPropagation(); // Prevent toggle when clicking color indicator
    if (editingColorGroup === groupKey) {
      setEditingColorGroup(null);
      setTempColor(null);
    } else {
      setEditingColorGroup(groupKey);
      setTempColor(currentColor);
    }
  };

  const styleField = sheetConfig?.styleColumn?.toLowerCase();
  const visibleCount = markers.filter(m => {
    if (!styleField) {
      const color = getMarkerColor(m.color);
      return !hiddenGroups.has(color);
    }
    const markerField = Object.keys(m).find(key => key.toLowerCase() === styleField);
    const value = markerField ? String(m[markerField] || 'None') : 'None';
    return !hiddenGroups.has(value);
  }).length;

  return (
    <div className="filter-panel">
      <div className="filter-header" onClick={() => setIsExpanded(!isExpanded)}>
        <div className="filter-title">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
            <path d="M1.5 3h13a.5.5 0 0 1 0 1h-13a.5.5 0 0 1 0-1zm0 4h13a.5.5 0 0 1 0 1h-13a.5.5 0 0 1 0-1zm0 4h13a.5.5 0 0 1 0 1h-13a.5.5 0 0 1 0-1z"/>
          </svg>
          <span>Grouping</span>
        </div>
        <div className="filter-summary">
          <span className="visible-count">{visibleCount} / {markers.length}</span>
          <span className={`expand-icon ${isExpanded ? 'expanded' : ''}`}>▼</span>
        </div>
      </div>

      {isExpanded && (
        <div className="filter-content">
          <div className="filter-controls">
            <button onClick={() => handleToggleAll(true)} className="filter-control-btn">
              Show All
            </button>
            <button onClick={() => handleToggleAll(false)} className="filter-control-btn">
              Hide All
            </button>
          </div>

          <div className="filter-groups">
            {colorGroups.map(group => {
              const isHidden = hiddenGroups.has(group.groupKey);
              const isEditingColor = editingColorGroup === group.groupKey;

              return (
                <div
                  key={group.groupKey}
                  className={`filter-group-item ${isHidden ? 'hidden' : ''}`}
                  onClick={() => handleToggleGroup(group.groupKey)}
                >
                  <input
                    type="checkbox"
                    checked={!isHidden}
                    onChange={() => {}}
                    className="group-checkbox"
                  />
                  <div className="group-color-wrapper">
                    <div
                      className="group-color-indicator"
                      style={{ backgroundColor: group.color }}
                      onClick={(e) => handleColorClick(group.groupKey, group.color, e)}
                      title="Click to change color"
                    />
                    {isEditingColor && (
                      <div className="color-picker-popup" onClick={(e) => e.stopPropagation()}>
                        <input
                          type="color"
                          value={tempColor || group.color}
                          onChange={(e) => handleColorInputChange(e.target.value, e)}
                          className="group-color-picker"
                        />
                        <button
                          onClick={(e) => handleSetColor(group.groupKey, e)}
                          className="set-color-btn"
                        >
                          Set Color
                        </button>
                      </div>
                    )}
                  </div>
                  <span className="group-label">{group.label}</span>
                  <span className="group-count">{group.markers.length}</span>
                </div>
              );
            })}
          </div>

          {colorGroups.length === 0 && (
            <div className="filter-empty">
              No color groups found. Configure colors in the sheet setup dialog.
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default FilterPanel;
